export const states = [
    { label: 'Alabama', value: 'Alabama' },
    { label: 'Alaska', value: 'Alaska' },
    { label: 'Arizona', value: 'Arizona' },
    { label: 'Arkansas', value: 'Arkansas' },
    { label: 'California', value: 'California' },
    { label: 'Colorado', value: 'Colorado' },
    { label: 'Connecticut', value: 'Connecticut' },
    { label: 'Delaware', value: 'Delaware' },
    { label: 'District of Columbia', value: 'District of Columbia'},    // Technically not a state, but needed for practices located in Washington DC
    { label: 'Florida', value: 'Florida' },
    { label: 'Georgia', value: 'Georgia' },
    { label: 'Hawaii', value: 'Hawaii' },
    { label: 'Idaho', value: 'Idaho' },
    { label: 'Illinois', value: 'Illinois' },
    { label: 'Indiana', value: 'Indiana' },
    { label: 'Iowa', value: 'Iowa' },
    { label: 'Kansas', value: 'Kansas' },
    { label: 'Kentucky', value: 'Kentucky' },
    { label: 'Louisiana', value: 'Louisiana' },
    { label: 'Maine', value: 'Maine' },
    { label: 'Maryland', value: 'Maryland' },
    { label: 'Massachusetts', value: 'Massachusetts' },
    { label: 'Michigan', value: 'Michigan' },
    { label: 'Minnesota', value: 'Minnesota' },
    { label: 'Mississippi', value: 'Mississippi' },
    { label: 'Missouri', value: 'Missouri' },
    { label: 'Montana', value: 'Montana' },
    { label: 'Nebraska', value: 'Nebraska' },
    { label: 'Nevada', value: 'Nevada' },
    { label: 'New Hampshire', value: 'New Hampshire' },
    { label: 'New Jersey', value: 'New Jersey' },
    { label: 'New Mexico', value: 'New Mexico' },
    { label: 'New York', value: 'New York' },
    { label: 'North Carolina', value: 'North Carolina' },
    { label: 'North Dakota', value: 'North Dakota' },
    { label: 'Ohio', value: 'Ohio' },
    { label: 'Oklahoma', value: 'Oklahoma' },
    { label: 'Oregon', value: 'Oregon' },
    { label: 'Pennsylvania', value: 'Pennsylvania' },
    { label: 'Rhode Island', value: 'Rhode Island' },
    { label: 'South Carolina', value: 'South Carolina' },
    { label: 'South Dakota', value: 'South Dakota' },
    { label: 'Tennessee', value: 'Tennessee' },
    { label: 'Texas', value: 'Texas' },
    { label: 'Utah', value: 'Utah' },
    { label: 'Vermont', value: 'Vermont' },
    { label: 'Virginia', value: 'Virginia' },
    { label: 'Washington', value: 'Washington' },
    { label: 'West Virginia', value: 'West Virginia' },
    { label: 'Wisconsin', value: 'Wisconsin' },
    { label: 'Wyoming', value: 'Wyoming' },
];

// US Territories - separate from states because territories have different legal status.
// Puerto Rico is included here (not in states array) since it's an unincorporated territory,
// not a state, yet still maps to country='United States' for address purposes.
export const usTerritories = [
    { label: 'Puerto Rico', value: 'Puerto Rico' },
];

export const canadaProvinces = [
    { label: 'Alberta', value: 'Alberta' },
    { label: 'British Columbia', value: 'British Columbia' },
    { label: 'Manitoba', value: 'Manitoba' },
    { label: 'New Brunswick', value: 'New Brunswick' },
    { label: 'Newfoundland and Labrador', value: 'Newfoundland and Labrador' },
    { label: 'Northwest Territories', value: 'Northwest Territories' },
    { label: 'Nova Scotia', value: 'Nova Scotia' },
    { label: 'Nunavut', value: 'Nunavut' },
    { label: 'Ontario', value: 'Ontario' },
    { label: 'Prince Edward Island', value: 'Prince Edward Island' },
    { label: 'Quebec', value: 'Quebec' },
    { label: 'Saskatchewan', value: 'Saskatchewan' },
    { label: 'Yukon', value: 'Yukon' },
];

// Bermuda's 9 parishes per ISO 3166-2:BM. Bermuda is its own country (British Overseas Territory),
// not a US territory - practices here map to country='Bermuda'.
// Note: City of Hamilton and Town of St. George are municipalities, not parishes.
export const bermudaParishes = [
    { label: 'Devonshire', value: 'Devonshire' },
    { label: 'Hamilton Parish', value: 'Hamilton Parish' },
    { label: 'Paget', value: 'Paget' },
    { label: 'Pembroke', value: 'Pembroke' },
    { label: 'Sandys', value: 'Sandys' },
    { label: "Smith's", value: "Smith's" },
    { label: 'Southampton', value: 'Southampton' },
    { label: "St. George's", value: "St. George's" },
    { label: 'Warwick', value: 'Warwick' },
];

/**
 * Grouped options for state/province dropdown with country optgroups.
 *
 * Structure notes:
 * - Each group becomes an <optgroup> in the select element
 * - `isHeader: true` marks items that render as disabled subsection dividers within an optgroup.
 *   HTML <select> doesn't support nested optgroups, so we use disabled options with visual
 *   styling (── markers) to create logical subsections (e.g., separating US states from territories).
 * - Header options have placeholder values (e.g., '__HEADER_US_TERRITORIES__') that cannot be selected.
 */
export const groupedStatesProvinces = [
    {
        label: 'United States',
        options: [
            ...states,
            { label: 'US Territories', value: '__HEADER_US_TERRITORIES__', isHeader: true },
            ...usTerritories,
        ],
    },
    {
        label: 'Canada',
        options: canadaProvinces,
    },
    {
        label: 'Bermuda',
        options: bermudaParishes,
    },
];

/**
 * Maps a state/province/territory/parish value to its parent country.
 * Used by practice create/edit forms to auto-populate the hidden country field
 * when user selects a region.
 *
 * @param {string} stateProvinceValue - The value from the stateProvince dropdown
 * @returns {string|null} 'United States' | 'Canada' | 'Bermuda' | null if unrecognized
 *
 * @example
 * getCountryFromStateProvince('Puerto Rico') // => 'United States' (territory)
 * getCountryFromStateProvince('Pembroke')    // => 'Bermuda' (parish)
 * getCountryFromStateProvince('Ontario')     // => 'Canada'
 * getCountryFromStateProvince('Unknown')     // => null
 */
export const getCountryFromStateProvince = (stateProvinceValue) => {
    if (!stateProvinceValue) return null;
    
    const usState = states.find(s => s.value === stateProvinceValue);
    if (usState) return 'United States';
    
    const usTerritory = usTerritories.find(t => t.value === stateProvinceValue);
    if (usTerritory) return 'United States';
    
    const caProvince = canadaProvinces.find(p => p.value === stateProvinceValue);
    if (caProvince) return 'Canada';
    
    const bermudaParish = bermudaParishes.find(p => p.value === stateProvinceValue);
    if (bermudaParish) return 'Bermuda';
    
    return null;
};
